import Vue from 'vue'
import { getProfileDetail, updateAccountProfileId, getContentReviewProfiles } from '../api'

const state = {
  activeProfile: {
    id: '',
    name: '',
    standard_sets: [],
    subjects: [],
    image_url: ''
  },
  _storedProfilesById: {},
  fetchingActiveProfile: false,
  updatingAccountProfile: false,
  isProfileSelectorOpen: false,
  contentReviewCustomization: null,
  contentReviewCriteria: null,
  fetchingContentReviewCustomization: false,
  isUsingCustomCriteria: false // tracks if using custom profile (not fallback to default)
}

export const mutations = {
  setActiveProfile(state, profile) {
    state.activeProfile = profile

    if (!state._storedProfilesById[profile.id]) {
      Vue.set(state._storedProfilesById, profile.id, profile)
    }
  },
  setFetchingActiveProfile(state, flag) {
    state.fetchingActiveProfile = flag
  },
  setUpdatingAccountProfile(state, flag) {
    state.updatingAccountProfile = flag
  },
  setIsProfileSelectorOpen(state, flag) {
    state.isProfileSelectorOpen = flag
  },
  storeProfile(state, profile) {
    if (profile && profile.id) {
      Vue.set(state._storedProfilesById, profile.id, profile)
    }
  },
  setContentReviewCustomization(state, criteriaSetName) {
    state.contentReviewCustomization = criteriaSetName
  },
  setContentReviewCriteria(state, criteria) {
    state.contentReviewCriteria = criteria
  },
  setIsUsingCustomCriteria(state, flag) {
    state.isUsingCustomCriteria = flag
  },
  setFetchingContentReviewCustomization(state, flag) {
    state.fetchingContentReviewCustomization = flag
  }
}

export const actions = {
  async getProfileDetail({ commit, state }, profileId) {
    if (state._storedProfilesById[profileId]) {
      console.info(`Returning stored profile info for ${profileId}`)
      return state._storedProfilesById[profileId]
    } else {
      let profileInfo = await getProfileDetail(profileId)
      commit('storeProfile', profileInfo)
      return profileInfo
    }
  },
  async setActiveProfileById({ dispatch, commit }, profileId) {
    if (!profileId) {
      return
    }

    commit('setFetchingActiveProfile', true)
    let profileInfo = await dispatch('getProfileDetail', profileId)
    commit('setFetchingActiveProfile', false)
    commit('setActiveProfile', profileInfo)
    return profileInfo
  },
  async updateAccountProfileId({ commit }, { userUuid, profileId }) {
    if (!profileId) {
      return
    }

    commit('setUpdatingAccountProfile', true)
    try {
      await updateAccountProfileId({ userUuid, profileId })
    } catch (err) {
      console.error(`Could not save account with selected content profile: ${JSON.stringify(err)}`)
    }
    commit('setUpdatingAccountProfile', false)
    return
  },
  async fetchContentReviewCustomization({ commit, state, rootGetters }) {
    const profileId = state.activeProfile?.id
    if (!profileId) {
      return
    }

    commit('setFetchingContentReviewCustomization', true)
    try {
      const profiles = await getContentReviewProfiles()
      const betaMode = Boolean(rootGetters?.['visibility/betaVisibility'])

      // Helper to check if a criteria item matches the release mode
      const criteriaMatchesRelease = (item) => {
        const isReleased = item.released !== false // default to true
        const isBetaOnly = item.beta_only === true
        return betaMode ? (isReleased || isBetaOnly) : (isReleased && !isBetaOnly)
      }

      // Helper to filter criteria by type based on release flags
      const filterCriteriaByType = (criteriaByType) => {
        if (!criteriaByType) return {}
        const result = {}
        for (const [type, items] of Object.entries(criteriaByType)) {
          result[type] = (items || []).filter(criteriaMatchesRelease)
        }
        return result
      }

      // Helper to check if any criteria are visible for a profile
      const hasVisibleCriteria = (profile) => {
        if (!profile?.criteria_by_type) return false
        return Object.values(profile.criteria_by_type).some(
          items => (items || []).some(criteriaMatchesRelease)
        )
      }

      // Find custom profile (ignore release flags at profile level)
      const userProfile = profiles.find(p => p.content_profile === profileId)

      // Use custom profile if it has visible criteria
      if (userProfile && hasVisibleCriteria(userProfile)) {
        commit('setContentReviewCustomization', userProfile.criteria_set_name || null)
        commit('setContentReviewCriteria', filterCriteriaByType(userProfile.criteria_by_type))
        commit('setIsUsingCustomCriteria', true)
        return userProfile
      }

      // Fall back to default profile
      const defaultProfile = profiles.find(p => p.content_profile === '')
      if (defaultProfile) {
        commit('setContentReviewCustomization', defaultProfile.criteria_set_name || null)
        commit('setContentReviewCriteria', filterCriteriaByType(defaultProfile.criteria_by_type))
        commit('setIsUsingCustomCriteria', false)
        return defaultProfile
      }

      // No profiles available
      commit('setContentReviewCustomization', null)
      commit('setContentReviewCriteria', null)
      commit('setIsUsingCustomCriteria', false)
    } catch (error) {
      console.error('Error fetching content review profiles:', error)
      commit('setContentReviewCustomization', null)
      commit('setContentReviewCriteria', null)
    } finally {
      commit('setFetchingContentReviewCustomization', false)
    }
  }
}

export const getters = {
  profileId(state) {
    return (state.activeProfile && state.activeProfile.id) || ''
  },
  profileImageUrl(state) {
    return (state.activeProfile && state.activeProfile.image_url) || ''
  },
  visibleStandardSets(state) {
    return (state.activeProfile && state.activeProfile.standard_sets) || ''
  },
  visibleStandardSetsForSubject(state) {
    let subjects = state.activeProfile.subjects || []
    return (subject, { onlyPublished }) => {
      if (!subject) {
        return []
      }
      let subjectInfo = subjects.find(s => {
        if (onlyPublished) {
          return s.subject === subject && s.published
        }
        return s.subject === subject
      })

      return (subjectInfo && subjectInfo.standard_sets) || []
    }
  },
  keyStandardSetForSubject(state) {
    let subjects = state.activeProfile.subjects || []
    return subject => {
      if (!subject) {
        return ''
      }
      let subjectInfo = subjects.find(s => s.subject === subject)

      return (subjectInfo && subjectInfo.key_standard_set) || ''
    }
  },
  staircaseStandardSetForSubject(state) {
    let subjects = state.activeProfile.subjects || []
    return subject => {
      if (!subject) {
        return ''
      }
      let subjectInfo = subjects.find(s => s.subject === subject)

      return (subjectInfo && subjectInfo.staircase_standard_set) || ''
    }
  },
  dropdownStandardSetForSubject(state) {
    var subjects = state.activeProfile.subjects || []
    return subject => {
      if (!subject) {
        return ''
      }
      let subjectInfo = subjects.find(s => s.subject === subject)

      return (subjectInfo && subjectInfo.dropdown_standard_set) || ''
    }
  },
  publishedSubjects(state) {
    return (
      (state.activeProfile &&
        state.activeProfile.subjects &&
        state.activeProfile.subjects.filter(subject => subject.published)) ||
      []
    )
  },
  standardSetLabelForSubjectCard(state) {
    var subjects = state.activeProfile.subjects || []
    return subject => {
      if (!subject) {
        return ''
      }
      let subjectInfo = subjects.find(s => s.subject === subject)

      return (subjectInfo && subjectInfo.standard_set_label) || ''
    }
  },
  contentReviewCriteriaSetName(state) {
    return state.contentReviewCustomization
  },
  contentReviewCriteria(state) {
    return state.contentReviewCriteria
  },
  isCustomCriteriaSet(state) {
    return state.isUsingCustomCriteria
  },
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
